// Phase 125-06 Task 1 — typed PostHog emit() wrapper for the app frontend.
//
// Contract (125-CONTEXT.md §8 app events + §16 canonical super properties):
//   Every app-frontend posthog.capture() call SHOULD go through this
//   wrapper. The wrapper:
//     1. Enforces the event registry from @hookmyapp/observability at the
//        type level (unknown event names fail tsc at the call site).
//     2. Merges the baseline ({ site: 'app', environment, days_since_signup })
//        into every capture so dashboards can filter reliably without
//        forcing every call site to remember the baseline keys.
//
//   The wrapper does NOT use @hookmyapp/observability's `emit()` at runtime
//   — that helper wraps posthog-node (server-side). Here we wrap posthog-js
//   (browser). Only the TYPES are shared.
//
// Baseline resolution:
//   - site: always 'app' (this module lives in the app frontend).
//   - environment: VITE_SENTRY_ENVIRONMENT (deploy-staging/production.yml
//     build-arg) → falls back to Vite's MODE ('production' in prod build,
//     'development' locally) → coerced into the three canonical buckets.
//   - days_since_signup: null for v1 (TODO: wire once posthog.getPerson()
//     API is consumed — CONTEXT §16 allows null for pre-signup events and
//     pre-wire).

import posthog from 'posthog-js';
import type { EventName, EventProperties } from '@hookmyapp/observability';

type Environment = 'staging' | 'production' | 'local';

function resolveEnvironment(): Environment {
  const env =
    (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ??
    import.meta.env.MODE;
  if (env === 'production') return 'production';
  if (env === 'staging') return 'staging';
  return 'local';
}

/**
 * Days since the person's `$set_once` `signup_date` super-property.
 *
 * Returns `null` until we wire the `posthog.getPerson()` read. Per CONTEXT
 * §16 the property IS allowed to be null for pre-signup events and v1
 * rollouts — flip this to a real computation once the person-properties
 * read lands in a follow-up plan.
 */
function computeDaysSinceSignup(): number | null {
  return null;
}

/**
 * Optional third argument for `emit()` — baseline overrides. The PostHog
 * baseline (CONTEXT §16) includes an optional `workspace_id` that the
 * wrapper cannot resolve from the event name alone, so expose it here.
 * Events whose per-event props already include `workspace_id` (Quick Start
 * cluster, invite_accepted, plan_checkout_completed, etc.) should pass
 * workspace_id on the event props and skip this override.
 */
export interface EmitContext {
  workspace_id?: string;
  /**
   * Optional PostHog groups map — e.g. `{ workspace: 'ws_abc' }` to attach
   * the event to a workspace group in PostHog. Mirrors `EmitContext.groups`
   * in `packages/observability/src/analytics/emitter.node.ts`, translated
   * to posthog-js's `$groups` event-property convention at the SDK boundary
   * below (posthog-js has no separate `groups` capture argument the way
   * posthog-node does).
   */
  groups?: Record<string, string>;
}

/**
 * Typed wrapper around `posthog.capture()`.
 *
 * Call-site shapes:
 *   emit('workspace_switched', { from_workspace_id, to_workspace_id }, { workspace_id });
 *   emit('quickstart_phone_copied', { workspace_id });  // workspace_id on props
 *
 * The baseline (`site`, `environment`, `days_since_signup`) is merged
 * automatically. The optional `ctx.workspace_id` is merged when present
 * and when the event doesn't already carry one on its per-event shape.
 */
export function emit<E extends EventName>(
  event: E,
  props: EventProperties<E>,
  ctx: EmitContext = {},
): void {
  const baseline: Record<string, unknown> = {
    site: 'app',
    environment: resolveEnvironment(),
    days_since_signup: computeDaysSinceSignup(),
  };
  if (ctx.workspace_id) baseline.workspace_id = ctx.workspace_id;
  if (ctx.groups) baseline.$groups = ctx.groups;
  try {
    posthog.capture(event, {
      ...baseline,
      ...(props as Record<string, unknown>),
    });
  } catch {
    // CLAUDE.md HARD RULE: observability fails-open. Never throw from a
    // capture site into user-facing code.
  }
}
